import { useEffect, useRef, useState } from 'react'
import Hls from 'hls.js'
import { X, ExternalLink, AlertTriangle } from 'lucide-react'

interface Props {
  open: boolean
  onClose: () => void
  title: string
  hlsUrl: string | null
}

export function VideoPlayerModal({ open, onClose, title, hlsUrl }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setError(null)
    if (!hlsUrl || !videoRef.current) return
    const video = videoRef.current

    let hls: Hls | null = null

    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = hlsUrl
    } else if (Hls.isSupported()) {
      hls = new Hls()
      hls.loadSource(hlsUrl)
      hls.attachMedia(video)
      hls.on(Hls.Events.ERROR, (_e, data) => {
        if (data.fatal) {
          console.error('HLS fatal error', data)
          setError('재생할 수 없습니다.')
        }
      })
    } else {
      setError('이 브라우저에서 HLS 재생을 지원하지 않습니다.')
    }

    return () => {
      try { video.pause() } catch { /* noop */ }
      video.removeAttribute('src')
      video.load()
      hls?.destroy()
    }
  }, [open, hlsUrl])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-4xl rounded-xl bg-black shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 text-white bg-black/40 rounded-t-xl">
          <h2 className="text-sm font-medium truncate">{title}</h2>
          <button
            onClick={onClose}
            aria-label="닫기"
            className="p-1 rounded hover:bg-white/10"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="aspect-video w-full bg-black">
          {error ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center text-white">
              <AlertTriangle className="w-8 h-8 text-amber-400" />
              <p className="text-sm">{error}</p>
              {hlsUrl && (
                <a
                  href={hlsUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-amber-300 hover:underline"
                >
                  <ExternalLink className="w-3.5 h-3.5" /> URL 새 탭에서 열기
                </a>
              )}
            </div>
          ) : (
            <video
              ref={videoRef}
              controls
              autoPlay
              playsInline
              className="w-full h-full"
            />
          )}
        </div>
      </div>
    </div>
  )
}
